import { type RefObject, useEffect, useState } from 'react';

/**
 * Tracks whether an element's content overflows vertically.
 * Useful for multi-line clamped text (e.g. noOfLines) where you want
 * to conditionally show a tooltip when content is truncated.
 *
 * Uses a ResizeObserver to re-check whenever the element's size changes.
 */
export const useHasVerticalOverflow = <T extends HTMLElement>(
  ref: RefObject<T | null>,
) => {
  const [isOverflowing, setIsOverflowing] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const checkOverflow = () =>
      setIsOverflowing(el.scrollHeight > el.clientHeight);

    checkOverflow();

    const observer = new ResizeObserver(checkOverflow);
    observer.observe(el);

    return () => observer.disconnect();
  }, [ref]);

  return isOverflowing;
};
